/**
 * Story 30.3 — registers the push worker (`public/sw.js`, Story 30.1) at
 * scope `/`. Memoised: one `register()` per page load. Resolves to the
 * ACTIVE registration (`navigator.serviceWorker.ready`), because
 * `pushManager.subscribe()` refuses a registration with no active worker.
 * `null` when this browser has no service worker API, or registration
 * failed (logged to the console, never thrown).
 *
 * Only files under `src/lib/push/` may touch the service worker API
 * (`scripts/__tests__/installable-app.test.ts`).
 */

export const SERVICE_WORKER_URL = '/sw.js';
export const SERVICE_WORKER_SCOPE = '/';

let pending: Promise<ServiceWorkerRegistration | null> | null = null;

function container(): ServiceWorkerContainer | null {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker ?? null;
}

export function registerPushWorker(): Promise<ServiceWorkerRegistration | null> {
  if (pending) return pending;
  const sw = container();
  if (!sw) return Promise.resolve(null);
  pending = sw
    .register(SERVICE_WORKER_URL, { scope: SERVICE_WORKER_SCOPE })
    .then(() => sw.ready)
    .catch((err: unknown) => {
      console.warn('push: worker registration failed', err instanceof Error ? err.message : err);
      pending = null;
      return null;
    });
  return pending;
}

/**
 * The existing registration for scope `/`, WITHOUT registering — logout uses
 * it so signing out never installs a worker.
 */
export async function existingPushWorker(): Promise<ServiceWorkerRegistration | null> {
  const sw = container();
  if (!sw) return null;
  try {
    return (await sw.getRegistration(SERVICE_WORKER_SCOPE)) ?? null;
  } catch {
    return null;
  }
}

/** Test seam: forget the memoised registration. */
export function resetPushWorkerForTests(): void {
  pending = null;
}
